import React, { useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { roleHome } from '../utils/roleHome';

/**
 * Bounce to the user's own dashboard AND say why.
 *
 * ⚠ The redirect itself is deliberate and stays: sending a wrong-role visitor
 * to "/" redirects to /login and clears the session, which reads as a random
 * logout. But it was SILENT — a sales consultant who clicked a deal link
 * simply found themselves on their dashboard with no idea what happened
 * (reproduced on Praxia staging, 2026-08-06). Silence is what makes a
 * permission boundary feel like a bug.
 *
 * The `id` dedupes: StrictMode mounts twice in development, and a redirect
 * chain must never stack notices.
 */
function RedirectWithNotice({ to }) {
  const announced = useRef(false);
  if (!announced.current) {
    announced.current = true;
    toast.error("You don't have access to that page", {
      id: 'route-not-allowed',
      description: "We've brought you back to your dashboard. Ask an admin if you need access.",
    });
  }
  return <Navigate to={to} replace />;
}

export default function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <RedirectWithNotice to={roleHome(user.role)} />;
  }

  return children;
}
